import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";
import { TrendingDown, TrendingUp, X, Loader2 } from "lucide-react";

const LS_KEY = "solano.goalReview.hideUntil";

export default function GoalReviewCard({ onApplied }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const until = +(localStorage.getItem(LS_KEY) || 0);
    if (until > Date.now()) return;
    api.get("/client/goal-review")
      .then(({ data }) => { if (data?.status === "suggest") setData(data); })
      .catch(() => {});
  }, []);

  if (!data || hidden) return null;
  const up = data.delta_kcal > 0;

  const apply = async () => {
    setSaving(true);
    try {
      await api.post("/client/goal-review/apply");
      toast.success(t("calc.reviewApplied"));
      setHidden(true);
      onApplied?.();
    } catch {
      toast.error(t("common.error"));
    } finally { setSaving(false); }
  };

  const later = () => {
    localStorage.setItem(LS_KEY, String(Date.now() + 7 * 864e5));
    setHidden(true);
  };

  return (
    <div className="rounded-2xl border border-[#5A8B8F]/30 bg-[#EAF3F4]/50 dark:bg-[#22383a]/60 p-3.5" data-testid="goal-review-card">
      <div className="flex items-start gap-2.5">
        <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${up ? "bg-emerald-100 text-emerald-600" : "bg-orange-100 text-orange-600"}`}>
          {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#2C5254] dark:text-slate-100">{t("calc.reviewTitle")}</p>
          <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5" data-testid="goal-review-body">
            {t("calc.reviewBody", { days: data.days_since, actual: data.actual_weekly_kg, expected: data.expected_weekly_kg })}
          </p>
          <p className="text-xs font-medium text-slate-700 dark:text-slate-200 mt-1">
            {t(up ? "calc.reviewSuggestUp" : "calc.reviewSuggestDown", { kcal: data.suggested_kcal })}
          </p>
        </div>
        <button type="button" onClick={later} data-testid="goal-review-dismiss" className="text-slate-400 hover:text-slate-600 shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-2.5 flex gap-2">
        <button
          type="button"
          onClick={apply}
          disabled={saving}
          data-testid="goal-review-apply"
          className="flex-1 h-9 rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
        >
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {t("calc.reviewApply")}
        </button>
        <button
          type="button"
          onClick={later}
          data-testid="goal-review-later"
          className="px-4 h-9 rounded-full border border-slate-300 text-slate-600 dark:text-slate-300 text-xs font-semibold"
        >
          {t("calc.reviewLater")}
        </button>
      </div>
    </div>
  );
}
